import React, { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { PageHeader, SectionCard } from "@/components/kit";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import {
  AlertDialog, AlertDialogTrigger, AlertDialogContent, AlertDialogHeader, AlertDialogTitle,
  AlertDialogDescription, AlertDialogFooter, AlertDialogCancel, AlertDialogAction,
} from "@/components/ui/alert-dialog";
import { toast } from "sonner";
import { Monitor, Smartphone, Tablet, MapPin, ShieldCheck, ExternalLink, LogOut, ShieldAlert } from "lucide-react";

const DeviceIcon = ({ type }) => {
  if (type === "Mobile") return <Smartphone className="h-4 w-4 text-navy" />;
  if (type === "Tablet") return <Tablet className="h-4 w-4 text-navy" />;
  return <Monitor className="h-4 w-4 text-navy" />;
};

const fmtWhen = (ts) => {
  try {
    return new Date(ts).toLocaleString("en-GB", {
      day: "2-digit", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit",
    });
  } catch { return ts; }
};

const permBadge = {
  granted: "bg-emerald-100 text-emerald-800 border-emerald-200",
  denied: "bg-red-100 text-red-700 border-red-200",
  unavailable: "bg-slate-100 text-slate-600 border-slate-200",
  timeout: "bg-amber-100 text-amber-800 border-amber-200",
  unknown: "bg-slate-100 text-slate-600 border-slate-200",
};

export default function SecurityActivity() {
  const { user } = useAuth();
  const qc = useQueryClient();
  const isAdmin = ["founder", "admin"].includes(user?.role);
  const [empFilter, setEmpFilter] = useState("all");

  const { data: sessions = [] } = useQuery({
    queryKey: ["my-sessions"],
    queryFn: async () => (await api.get("/auth/sessions")).data,
  });

  const logoutAll = useMutation({
    mutationFn: async () => (await api.post("/auth/logout-all")).data,
    onSuccess: (d) => {
      qc.invalidateQueries({ queryKey: ["my-sessions"] });
      toast.success(d.revoked > 0 ? `Signed out of ${d.revoked} other device(s).` : "No other active sessions.");
    },
    onError: () => toast.error("Could not sign out other devices."),
  });

  const { data: employees = [] } = useQuery({
    queryKey: ["employees"],
    queryFn: async () => (await api.get("/employees")).data,
    enabled: isAdmin,
  });

  const { data: rows = [], isLoading } = useQuery({
    queryKey: ["login-activity", empFilter],
    queryFn: async () =>
      (await api.get("/security/login-activity", {
        params: empFilter && empFilter !== "all" ? { employee_id: empFilter } : {},
      })).data,
  });

  if (isLoading)
    return (
      <div>
        <PageHeader title="Login Activity" subtitle="Device & location of every sign-in" />
        <Skeleton className="h-64 rounded-lg" />
      </div>
    );

  return (
    <div data-testid="security-activity-page">
      <PageHeader
        title="Login Activity"
        subtitle={isAdmin ? "Security log — device & location of every sign-in" : "Your recent sign-ins across devices"}
      >
        {isAdmin && (
          <Select value={empFilter} onValueChange={setEmpFilter}>
            <SelectTrigger className="w-56" data-testid="security-employee-filter">
              <SelectValue placeholder="All employees" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All employees</SelectItem>
              {employees.map((e) => (
                <SelectItem key={e.employee_id} value={e.employee_id}>{e.name} ({e.employee_id})</SelectItem>
              ))}
            </SelectContent>
          </Select>
        )}
      </PageHeader>

      <SectionCard
        title="Active Sessions"
        testId="active-sessions"
        action={
          <AlertDialog>
            <AlertDialogTrigger asChild>
              <Button variant="outline" size="sm" data-testid="signout-all-btn" className="rounded-lg border-red-200 text-red-600 hover:bg-red-50">
                <LogOut className="mr-2 h-4 w-4" /> Sign out of all other devices
              </Button>
            </AlertDialogTrigger>
            <AlertDialogContent>
              <AlertDialogHeader>
                <AlertDialogTitle className="font-heading text-navy">Sign out of all other devices?</AlertDialogTitle>
                <AlertDialogDescription>
                  This ends every session except the one you are using now. You will stay signed in here,
                  but all other browsers and devices will be signed out immediately.
                </AlertDialogDescription>
              </AlertDialogHeader>
              <AlertDialogFooter>
                <AlertDialogCancel data-testid="signout-all-cancel">Cancel</AlertDialogCancel>
                <AlertDialogAction onClick={() => logoutAll.mutate()} data-testid="signout-all-confirm" className="bg-red-600 hover:bg-red-700 text-white">
                  Yes, sign them out
                </AlertDialogAction>
              </AlertDialogFooter>
            </AlertDialogContent>
          </AlertDialog>
        }
      >
        <div className="space-y-3" data-testid="sessions-list">
          {sessions.map((s) => (
            <div key={s.id} className="flex items-center justify-between rounded-lg border border-slate-200 px-4 py-3" data-testid={`session-${s.id}`}>
              <div className="flex items-center gap-3">
                <DeviceIcon type={s.device_type} />
                <div>
                  <div className="text-sm font-medium text-navy">
                    {s.browser} · {s.os}
                    {s.current && <Badge className="ml-2 bg-emerald-100 text-emerald-800 border-emerald-200">This device</Badge>}
                  </div>
                  <div className="text-xs text-slate-400">
                    {s.ip || "—"} · last active {fmtWhen(s.last_activity)}
                  </div>
                </div>
              </div>
            </div>
          ))}
          {sessions.length === 0 && <p className="text-sm text-slate-400">No active sessions.</p>}
        </div>
      </SectionCard>

      <SectionCard title="Sign-in History" testId="security-list">
        {rows.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-14 text-center" data-testid="security-empty">
            <div className="rounded-full bg-navy/5 p-4 mb-3"><ShieldCheck className="h-8 w-8 text-navy" /></div>
            <p className="text-sm font-medium text-slate-500">No login activity recorded yet</p>
            <p className="mt-1 text-xs text-slate-400">Device and location are captured on each sign-in.</p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>When</TableHead>
                  {isAdmin && <TableHead>User</TableHead>}
                  <TableHead>Device</TableHead>
                  <TableHead>IP Address</TableHead>
                  <TableHead>Location</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {rows.map((r) => (
                  <TableRow key={r.activity_id} data-testid={`security-row-${r.activity_id}`}>
                    <TableCell className="whitespace-nowrap text-sm text-slate-700">{fmtWhen(r.timestamp)}</TableCell>
                    {isAdmin && (
                      <TableCell>
                        <div className="text-sm font-medium text-navy">{r.name || "—"}</div>
                        <div className="text-xs text-slate-400">{r.email}</div>
                      </TableCell>
                    )}
                    <TableCell>
                      <div className="flex items-center gap-2">
                        <DeviceIcon type={r.device_type} />
                        <div>
                          <div className="text-sm text-slate-700">{r.browser} · {r.os}</div>
                          <div className="text-xs text-slate-400">{r.device_type}</div>
                        </div>
                        {(r.is_new_device || r.is_new_location) && (
                          <Badge className="ml-1 bg-amber-100 text-amber-800 border-amber-200" title="Anomalous sign-in">
                            <ShieldAlert className="mr-1 h-3 w-3" />
                            {r.is_new_device && r.is_new_location ? "New device & location" : r.is_new_device ? "New device" : "New location"}
                          </Badge>
                        )}
                      </div>
                    </TableCell>
                    <TableCell className="text-sm text-slate-600 font-mono">{r.ip || "—"}</TableCell>
                    <TableCell className="max-w-xs">
                      {r.latitude != null && r.longitude != null ? (
                        <div>
                          <div className="text-sm text-slate-700 truncate" title={r.location_label}>
                            {r.location_label || `${r.latitude.toFixed(4)}, ${r.longitude.toFixed(4)}`}
                          </div>
                          <a
                            href={`https://www.google.com/maps?q=${r.latitude},${r.longitude}`}
                            target="_blank"
                            rel="noreferrer"
                            data-testid={`security-map-${r.activity_id}`}
                            className="inline-flex items-center gap-1 text-xs text-gold-dark hover:underline"
                          >
                            <MapPin className="h-3 w-3" /> View on map <ExternalLink className="h-3 w-3" />
                          </a>
                        </div>
                      ) : (
                        <Badge variant="outline" className={permBadge[r.location_permission] || permBadge.unknown}>
                          {r.location_permission === "denied" ? "Location denied" : "No location"}
                        </Badge>
                      )}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        )}
      </SectionCard>
    </div>
  );
}
